import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@/components/toast';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useDataLayer } from '@/hooks/use-data-layer';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link as LinkIcon, Mail, MessageSquare, Copy, type LucideIcon } from 'lucide-react';
import { SvgShare } from '../icons-eataly';
import type { DataLayerEventPayloads } from '@/lib/analytics/data-layer';
import type { SCAPIShopperProductsSchemas } from '@/types';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

type ShareAnalyticsMethod = DataLayerEventPayloads['share']['method'];
type ShareAnalyticsContentType = DataLayerEventPayloads['share']['content_type'];

interface ShareButtonProps {
    product: SCAPIShopperProductsSchemas['Product'];
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    tabIndex?: number;
}

type ShareProvider = 'Twitter' | 'Facebook' | 'LinkedIn' | 'Email';

// Provider icon mapping (constant, defined outside component)
const providerIcons: Record<ShareProvider, LucideIcon> = {
    Twitter: MessageSquare,
    Facebook: LinkIcon,
    LinkedIn: LinkIcon,
    Email: Mail,
};

// Provider label mapping (constant, defined outside component)
const providerLabels: Record<ShareProvider, string> = {
    Twitter: 'Twitter/X',
    Facebook: 'Facebook',
    LinkedIn: 'LinkedIn',
    Email: 'Email',
};

/**
 * Share button that triggers native share dialog if available, otherwise shows dropdown menu.
 * Share options are configurable via config.features.socialShare config.
 *
 * @param props - Component props
 * @param props.product - The product data to share
 * @param props.size - Icon size (sm, md, lg)
 * @param props.className - Optional additional CSS classes
 * @param props.tabIndex - Tab index for accessibility
 * @returns JSX element with share button
 */
export function ShareButton({ product, className, tabIndex }: ShareButtonProps): ReactElement {
    const { addToast } = useToast();
    const config = useConfig();
    const dataLayer = useDataLayer();
    const [open, setOpen] = useState(false);
    const [hasNativeShare, setHasNativeShare] = useState(false);
    const { t } = useTranslation('account');

    const shareContentType: ShareAnalyticsContentType | undefined =
        product.c_productType === 'course' ? 'class' : product.c_productType === 'event' ? 'event' : undefined;
    const itemId = product.id;

    const trackShare = useCallback(
        (method: ShareAnalyticsMethod) => {
            if (!shareContentType || !itemId) {
                return;
            }

            dataLayer.track({
                event: 'share',
                method,
                content_type: shareContentType,
                item_id: itemId,
                event_type: 'ga4_ce',
            });
        },
        [dataLayer, itemId, shareContentType]
    );

    const productName = product.name || 'Check out this product';
    const productDescription = product.shortDescription || 'I found this great product';
    // Safely access window.location.href - only on client side
    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(productName);
    const encodedText = encodeURIComponent(`${productName}\n\n${productDescription}`);

    useEffect(() => {
        setHasNativeShare(
            typeof navigator !== 'undefined' && 'share' in navigator && typeof navigator.share === 'function'
        );
    }, []);

    const handleNativeShare = async () => {
        if (!hasNativeShare) {
            addToast('Native sharing is not available on this device', 'error');
            return;
        }

        try {
            const shareMessage = `${productName}\n\n${productDescription}\n\n${shareUrl}`;
            await navigator.share({
                title: productName,
                text: shareMessage,
                url: shareUrl,
            });
            trackShare('native');
        } catch (error) {
            // User cancelled - silently fail
            if (error instanceof Error && error.name !== 'AbortError') {
                addToast('Failed to share', 'error');
            }
        }
    };

    const handleClick = () => {
        if (hasNativeShare) {
            void handleNativeShare();
        }
        // When native share is unavailable, DropdownMenu handles state via onOpenChange
    };

    const handleCopyLink = async () => {
        trackShare('copy link');

        try {
            // Only copy the URL, not the full share text
            await navigator.clipboard.writeText(shareUrl);
            addToast('Link copied to clipboard', 'success');
        } catch {
            addToast('Failed to copy link', 'error');
        }
    };

    // Share provider handlers
    const shareHandlers = useMemo(
        () => ({
            Email: () => {
                trackShare('email');
                const subject = encodedTitle;
                const body = `${encodedText}%0A%0A${encodedUrl}`;
                window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
            },
            Twitter: () => {
                trackShare('twitter');
                const text = `${productName} - ${shareUrl}`;
                window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodedUrl}`,
                    '_blank'
                );
            },
            Facebook: () => {
                trackShare('facebook');
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
            },
            LinkedIn: () => {
                trackShare('linkedin');
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
            },
        }),
        [encodedText, encodedTitle, encodedUrl, productName, shareUrl, trackShare]
    );

    // Build share provider configurations
    const shareProviders = useMemo(() => {
        const enabledProviders = config.features.socialShare.enabled ? config.features.socialShare.providers : [];

        return enabledProviders
            .filter((provider): provider is ShareProvider => {
                return ['Twitter', 'Facebook', 'LinkedIn', 'Email'].includes(provider);
            })
            .map((provider) => ({
                provider,
                icon: providerIcons[provider],
                label: providerLabels[provider],
                handler: shareHandlers[provider],
            }));
    }, [config.features.socialShare.enabled, config.features.socialShare.providers, shareHandlers]);

    // If native share is available, render just the icon button
    if (hasNativeShare) {
        return (
            <Button hierarchy="tertiary" iconOnly aria-label="Share" onClick={handleClick} tabIndex={tabIndex}>
                <SvgShare className={className} />{' '}
            </Button>
        );
    }

    // Otherwise, render dropdown menu with fallback options
    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild onClick={() => setOpen(!open)}>
                <button tabIndex={tabIndex}>
                    <SvgShare className={className} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem>
                    <Button variant="ghost" onClick={() => void handleCopyLink()}>
                        <Copy className="size-4" aria-hidden="true" />
                        <span>{t('mgm.share.copyLink')}</span>
                    </Button>
                </DropdownMenuItem>
                {shareProviders.length > 0 && (
                    <>
                        {shareProviders.some((p) => p.provider === 'Email') && (
                            <>
                                <DropdownMenuSeparator />
                                {shareProviders
                                    .filter((p) => p.provider === 'Email')
                                    .map(({ provider, icon: Icon, label, handler }) => (
                                        <DropdownMenuItem key={provider}>
                                            <Button variant="ghost" onClick={handler}>
                                                <Icon className="size-4" aria-hidden="true" />
                                                <span>{label}</span>
                                            </Button>
                                        </DropdownMenuItem>
                                    ))}
                            </>
                        )}
                        {shareProviders.some((p) => p.provider !== 'Email') && (
                            <>
                                <DropdownMenuSeparator />
                                {shareProviders
                                    .filter((p) => p.provider !== 'Email')
                                    .map(({ provider, icon: Icon, label, handler }) => (
                                        <DropdownMenuItem key={provider}>
                                            <Button variant="ghost" onClick={handler}>
                                                <Icon className="size-4" aria-hidden="true" />
                                                <span>{label}</span>
                                            </Button>
                                        </DropdownMenuItem>
                                    ))}
                            </>
                        )}
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
